// The one composer of every chat (decision D36, docs/delivery/composer-and-simplicity-plan.md section A): the side
// assistant, the Home chat and the Home brief. Presentational and controlled: the parent owns the text, the attachments
// and what a submit or a command does. Here: Enter sends, Shift+Enter is a new line and an IME composition never sends;
// attach button, drag and drop and paste of files/images; a pasted text longer than LONG_PASTE_CHARS becomes a .txt
// attachment; the character counter with its limit; microphone dictation (fills the field, never sends); and the
// slash-command autocomplete (ARIA combobox + listbox, keyboard navigable).
import { useId, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { useI18n } from "../../i18n/index.tsx";
import { IconButton } from "../../components/Button.tsx";
import { PaperclipIcon, type IconProps } from "../../components/icons.tsx";
import { AttachmentChips, type ChipItem } from "./AttachmentChips.tsx";
import { useDictation } from "./dictation.ts";
import { matchSlash, slashQuery, type SlashCommand } from "./slash.ts";

/** A pasted text longer than this becomes a text attachment instead of filling the field (like ChatGPT). */
export const LONG_PASTE_CHARS = 4000;

function MicIcon({ size = 18, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
    </svg>
  );
}

export interface ComposerProps {
  /** The textarea id (its label is `label`, visually hidden). */
  id: string;
  label: string;
  placeholder?: string;
  /** Screen-reader hint (e.g. "Enter sends; Shift+Enter adds a line"). */
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  /** Enter without Shift and outside an IME composition. The parent decides (send, run a command…). */
  onSubmit: () => void;
  maxChars: number;
  tooLongMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  inputRef?: Ref<HTMLTextAreaElement>;
  rows?: number;
  /** Attachments: chips, plus attach / drop / paste when `onAddFiles` is set. */
  attachments?: readonly ChipItem[];
  onAddFiles?: (files: File[]) => void;
  onRemoveAttachment?: (key: string) => void;
  accept?: string;
  multiple?: boolean;
  attachLabel?: string;
  fileInputTestId?: string;
  /** Title hint of the attach button (default: the caps of composer attachments; "" for none). */
  attachHint?: string;
  /** Long pasted text → a `.txt` attachment (needs `onAddFiles`). */
  longPaste?: boolean;
  /** Slash commands offered by the autocomplete menu; picking one calls `onSlashCommand` with no argument. */
  slashCommands?: readonly SlashCommand[];
  onSlashCommand?: (command: SlashCommand, rest: string) => void;
  dictation?: boolean;
  /** Send / Stop buttons (end of the toolbar). */
  actions?: ReactNode;
  /** Extra controls in the toolbar (the Home chat's selectors). */
  controls?: ReactNode;
  /** Notices or panels shown above the field (command help, integrations…). */
  above?: ReactNode;
  counterTestId?: string;
  className?: string;
  inputClassName?: string;
}

export function Composer(props: ComposerProps) {
  const { t, locale } = useI18n();
  const {
    id,
    value,
    onChange,
    maxChars,
    attachments = [],
    onAddFiles,
    slashCommands,
    onSlashCommand,
    disabled = false,
  } = props;
  const uid = useId();
  const counterId = `${uid}-counter`;
  const hintId = `${uid}-hint`;
  const listId = `${uid}-commands`;
  const fileInput = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const pasted = useRef(0);
  const [note, setNote] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);

  const dictation = useDictation(locale, (text) => {
    const current = valueRef.current;
    onChange(current && !/\s$/.test(current) ? `${current} ${text}` : `${current}${text}`);
  });

  const query = slashCommands && onSlashCommand ? slashQuery(value) : null;
  const matches = query !== null && slashCommands ? matchSlash(query, slashCommands) : [];
  const menuOpen = !disabled && matches.length > 0 && dismissed !== value;
  const activeIndex = Math.min(active, Math.max(0, matches.length - 1));

  const choose = (command: SlashCommand) => {
    setDismissed(null);
    setActive(0);
    onChange("");
    onSlashCommand?.(command, "");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActive((activeIndex + step + matches.length) % matches.length);
        return;
      }
      if ((e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) || e.key === "Tab") {
        e.preventDefault();
        const picked = matches[activeIndex];
        if (picked) choose(picked);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setDismissed(value);
        return;
      }
    }
    // IME: a composing Enter (isComposing, or keyCode 229 on some browsers) confirms the composition, never sends.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
      e.preventDefault();
      props.onSubmit();
    }
  };

  const addFiles = (files: File[]) => {
    if (files.length > 0 && onAddFiles) onAddFiles(files);
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onAddFiles) return;
    const files = [...e.clipboardData.files];
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
      return;
    }
    const text = e.clipboardData.getData("text/plain");
    if (props.longPaste && text.length > LONG_PASTE_CHARS) {
      e.preventDefault();
      pasted.current += 1;
      const name = t("composer.pastedName", { n: pasted.current });
      addFiles([new File([text], name, { type: "text/plain" })]);
      setNote(t("composer.pastedNote", { name }));
    }
  };

  const hasFiles = (e: DragEvent) => !!onAddFiles && [...e.dataTransfer.types].includes("Files");
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(e) || disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(e) || disabled) return;
    e.preventDefault();
    setDragging(false);
    addFiles([...e.dataTransfer.files]);
  };

  const attachLabel = props.attachLabel ?? t("composer.attach");
  const attachHint = props.attachHint ?? t("composer.attachHint");
  const over = value.length > maxChars;
  const describedBy = [props.hint ? hintId : null, counterId].filter(Boolean).join(" ");
  const micLabel = !dictation.supported ? t("composer.mic.unavailable") : dictation.listening ? t("composer.mic.stop") : t("composer.mic.start");

  return (
    <div className={`cmp${dragging ? " cmp--dragging" : ""}${props.className ? ` ${props.className}` : ""}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} data-testid="composer">
      {props.above}
      <div className="cmp-field">
        <label htmlFor={id} className="sr-only">
          {props.label}
        </label>
        <textarea
          id={id}
          ref={props.inputRef}
          className={`cmp-input${props.inputClassName ? ` ${props.inputClassName}` : ""}`}
          rows={props.rows ?? 2}
          value={value}
          placeholder={props.placeholder}
          disabled={disabled}
          aria-invalid={over || props.invalid || undefined}
          aria-describedby={describedBy}
          {...(slashCommands && onSlashCommand
            ? {
                "aria-autocomplete": "list" as const,
                ...(menuOpen
                  ? { role: "combobox", "aria-expanded": true, "aria-controls": listId, "aria-activedescendant": `${listId}-${matches[activeIndex]?.name ?? ""}` }
                  : {}),
              }
            : {})}
          onChange={(e) => {
            setNote(null);
            if (dismissed !== null && e.target.value !== dismissed) setDismissed(null);
            setActive(0);
            onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        {menuOpen ? (
          <ul id={listId} role="listbox" className="cmp-commands" aria-label={t("composer.slash.menu")} data-testid="composer-commands">
            {matches.map((c, i) => (
              <li
                key={c.name}
                id={`${listId}-${c.name}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`cmp-command${i === activeIndex ? " cmp-command--active" : ""}`}
                // mousedown: pick before the textarea loses focus.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(c);
                }}
              >
                <span className="cmp-command-name">/{c.name}</span>
                <span className="cmp-command-desc subtle">{t(c.description)}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {props.hint ? (
        <p id={hintId} className="sr-only">
          {props.hint}
        </p>
      ) : null}
      <AttachmentChips items={attachments} {...(props.onRemoveAttachment ? { onRemove: props.onRemoveAttachment } : {})} disabled={disabled} testId="composer-attachments" />
      {dragging ? (
        <p className="cmp-drop" aria-hidden="true">
          {t("composer.dropHere")}
        </p>
      ) : null}
      <div className="cmp-toolbar">
        {onAddFiles ? (
          <>
            <IconButton label={attachLabel} title={attachHint ? `${attachLabel} — ${attachHint}` : attachLabel} onClick={() => fileInput.current?.click()} disabled={disabled}>
              <PaperclipIcon size={18} />
            </IconButton>
            <input
              ref={fileInput}
              type="file"
              multiple={props.multiple ?? true}
              {...(props.accept ? { accept: props.accept } : {})}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              data-testid={props.fileInputTestId ?? "composer-file-input"}
              onChange={(e) => {
                addFiles([...(e.target.files ?? [])]);
                e.target.value = "";
              }}
            />
          </>
        ) : null}
        {props.dictation ? (
          <IconButton
            label={micLabel}
            aria-pressed={dictation.supported ? dictation.listening : undefined}
            aria-disabled={!dictation.supported || disabled || undefined}
            className={dictation.listening ? "cmp-mic cmp-mic--on" : "cmp-mic"}
            data-testid="composer-mic"
            onClick={() => {
              if (!dictation.supported) {
                setNote(t("composer.mic.unavailable"));
                return;
              }
              if (disabled) return;
              if (dictation.listening) dictation.stop();
              else dictation.start();
            }}
          >
            <MicIcon size={18} />
          </IconButton>
        ) : null}
        {props.controls}
        <span className="spacer" />
        {props.actions}
      </div>
      <div className="cmp-foot">
        <p id={counterId} className={`cmp-counter${over ? " cmp-counter--over" : ""}`} data-testid={props.counterTestId ?? "composer-counter"}>
          {t("assistant.counter", { n: value.length, max: maxChars })}
        </p>
        <div className="cmp-status" role="status" aria-live="polite">
          {dictation.listening ? <span className="cmp-listening">{t("composer.mic.listening")}</span> : null}
          {dictation.state === "failed" ? <span>{t("composer.mic.failed")}</span> : null}
          {note ? <span>{note}</span> : null}
        </div>
      </div>
      {over ? (
        <p className="cmp-counter cmp-counter--over" role="alert">
          {props.tooLongMessage ?? t("assistant.tooLong", { max: maxChars })}
        </p>
      ) : null}
    </div>
  );
}
